/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { driftService, DriftReport } from '../../services/registryApi';
import toast from 'react-hot-toast';

/* ── helpers ─────────────────────────────────────────────────────────── */

const parseFeatureScores = (
  raw: string | Record<string, number>,
): Record<string, number> => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw as Record<string, number>;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

type FeatureStatus = 'no_drift' | 'warning' | 'drift';

const featureStatus = (score: number): FeatureStatus => {
  if (score >= 0.2) return 'drift';
  if (score >= 0.1) return 'warning';
  return 'no_drift';
};

const featureStatusBadge: Record<
  FeatureStatus,
  { label: string; bg: string; text: string }
> = {
  no_drift: { label: 'No Drift', bg: 'bg-green-500/20', text: 'text-green-400' },
  warning: { label: 'Warning', bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  drift: { label: 'Drift', bg: 'bg-red-500/20', text: 'text-red-400' },
};

/**
 * Simple CSS bar chart component. Renders a set of bars using div widths.
 * Values are normalized against the max value in the array.
 */
const MiniBarChart: React.FC<{
  label: string;
  values: number[];
  colorClass: string;
}> = ({ label, values, colorClass }) => {
  const maxVal = Math.max(...values, 0.001);
  return (
    <div className="flex-1">
      <p className="text-xs text-gray-400 mb-2 font-medium">{label}</p>
      <div className="space-y-1">
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500 w-5 text-right font-mono">
              {i + 1}
            </span>
            <div className="flex-1 bg-gray-800 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full ${colorClass}`}
                style={{ width: `${Math.max((v / maxVal) * 100, 2)}%` }}
              />
            </div>
            <span className="text-[10px] text-gray-500 font-mono w-10 text-right">
              {v.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Generate placeholder distribution buckets for a feature.
 * In production this data would come from the stored report in MinIO.
 */
const generatePlaceholderDistribution = (
  featureName: string,
  seed: number,
): { baseline: number[]; current: number[] } => {
  // Deterministic-ish pseudo-random based on feature name + seed
  const hash = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (h << 5) - h + s.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };
  const h = hash(featureName + seed);
  const buckets = 8;
  const baseline: number[] = [];
  const current: number[] = [];
  for (let i = 0; i < buckets; i++) {
    baseline.push(((h * (i + 3) * 17) % 100) / 100);
    current.push(((h * (i + 7) * 13 + seed * 31) % 100) / 100);
  }
  // Normalize
  const sumB = baseline.reduce((a, b) => a + b, 0) || 1;
  const sumC = current.reduce((a, b) => a + b, 0) || 1;
  return {
    baseline: baseline.map((v) => v / sumB),
    current: current.map((v) => v / sumC),
  };
};

/* ── component ───────────────────────────────────────────────────────── */

const DriftReportDetail: React.FC = () => {
  const { id, reportId } = useParams<{ id: string; reportId: string }>();
  const navigate = useNavigate();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['drift-report', reportId],
    queryFn: () => driftService.getReport(reportId!).then((r) => r.data),
    enabled: !!reportId,
  });

  const report: DriftReport | undefined = reportData?.report;

  const featureScores = useMemo(
    () => (report ? parseFeatureScores(report.feature_scores) : {}),
    [report],
  );

  const featureEntries = useMemo(
    () =>
      Object.entries(featureScores).sort(
        ([, a], [, b]) => (b as number) - (a as number),
      ),
    [featureScores],
  );

  /* ---- render ---- */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Drift report not found.</p>
      </div>
    );
  }

  const truncatedId = report.id.length > 12
    ? `${report.id.slice(0, 12)}...`
    : report.id;

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <button
          onClick={() => navigate(`/registry/drift/${id}`)}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Monitor
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="w-7 h-7 text-orange-500" />
                <h1 className="text-2xl font-bold text-white">
                  Report {truncatedId}
                </h1>
              </div>
              <p className="text-sm text-gray-400 mt-1">
                Checked at{' '}
                {new Date(report.checked_at).toLocaleString()}
              </p>
            </div>
          </div>
          {report.drift_detected ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full bg-red-500/20 text-red-400">
              <XCircleIcon className="w-4 h-4" />
              Drift Detected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full bg-green-500/20 text-green-400">
              <CheckCircleIcon className="w-4 h-4" />
              No Drift
            </span>
          )}
        </div>

        {/* Summary Card */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-gray-500">Overall Score</p>
              <p className="text-xl font-bold text-white mt-1">
                {report.overall_score.toFixed(4)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Sample Count</p>
              <p className="text-xl font-bold text-white mt-1">
                {report.sample_count.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Baseline Count</p>
              <p className="text-xl font-bold text-white mt-1">
                {report.baseline_count.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Action Taken</p>
              <p className="text-sm font-medium text-white mt-1">
                {report.action_taken || 'None'}
              </p>
            </div>
          </div>
        </div>

        {/* Concept Drift Section */}
        {report.concept_drift !== null && report.concept_drift !== undefined && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
              Concept Drift
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-800/60 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Concept Drift</p>
                {report.concept_drift ? (
                  <span className="inline-flex items-center gap-1 text-red-400 font-medium">
                    <XCircleIcon className="w-4 h-4" /> Detected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-green-400 font-medium">
                    <CheckCircleIcon className="w-4 h-4" /> Not Detected
                  </span>
                )}
              </div>
              {report.accuracy_baseline !== null &&
                report.accuracy_baseline !== undefined && (
                  <div className="bg-gray-800/60 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">
                      Accuracy (Baseline)
                    </p>
                    <p className="text-xl font-bold text-white">
                      {(report.accuracy_baseline * 100).toFixed(2)}%
                    </p>
                  </div>
                )}
              {report.accuracy_current !== null &&
                report.accuracy_current !== undefined && (
                  <div className="bg-gray-800/60 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">
                      Accuracy (Current)
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-xl font-bold text-white">
                        {(report.accuracy_current * 100).toFixed(2)}%
                      </p>
                      {report.accuracy_baseline !== null &&
                        report.accuracy_baseline !== undefined && (
                          <>
                            {report.accuracy_current < report.accuracy_baseline ? (
                              <ArrowTrendingDownIcon className="w-5 h-5 text-red-400" />
                            ) : (
                              <ArrowTrendingUpIcon className="w-5 h-5 text-green-400" />
                            )}
                            <span
                              className={`text-xs font-medium ${
                                report.accuracy_current < report.accuracy_baseline
                                  ? 'text-red-400'
                                  : 'text-green-400'
                              }`}
                            >
                              {(
                                (report.accuracy_current -
                                  report.accuracy_baseline) *
                                100
                              ).toFixed(2)}
                              pp
                            </span>
                          </>
                        )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Per-Feature Breakdown */}
        {featureEntries.length > 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-6">
              Per-Feature Breakdown
            </h2>
            <div className="space-y-6">
              {featureEntries.map(([feature, score], idx) => {
                const status = featureStatus(score);
                const badge = featureStatusBadge[status];
                const dist = generatePlaceholderDistribution(feature, idx);

                return (
                  <div
                    key={feature}
                    className="bg-gray-800/40 rounded-lg p-5 border border-gray-800"
                  >
                    {/* Feature Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <ChartBarIcon className="w-5 h-5 text-gray-500" />
                        <h3 className="text-sm font-semibold text-white font-mono">
                          {feature}
                        </h3>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono text-gray-300">
                          {score.toFixed(4)}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${badge.bg} ${badge.text}`}
                        >
                          {badge.label}
                        </span>
                      </div>
                    </div>

                    {/* Distribution Comparison */}
                    <div className="flex gap-6">
                      <MiniBarChart
                        label="Baseline Distribution"
                        values={dist.baseline}
                        colorClass="bg-blue-500"
                      />
                      <MiniBarChart
                        label="Current Distribution"
                        values={dist.current}
                        colorClass="bg-orange-500"
                      />
                    </div>
                    <p className="text-[10px] text-gray-600 mt-2">
                      Distribution visualizations are placeholders until full data
                      is loaded from storage.
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pipeline Run Link */}
        {report.pipeline_run_id && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
              Triggered Pipeline Run
            </h2>
            <div className="flex items-center gap-3">
              <BoltIcon className="w-5 h-5 text-orange-500" />
              <Link
                to={`/registry/pipelines/runs/${report.pipeline_run_id}`}
                className="text-orange-400 hover:text-orange-300 text-sm font-medium font-mono transition-colors"
              >
                {report.pipeline_run_id}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriftReportDetail;
